import type { ComponentType, CSSProperties } from 'react';
import { FacebookFilled, InstagramOutlined, YoutubeFilled } from '@ant-design/icons';

import type { SiteSettings } from '@store/slices/settingsSlice.tsx';

type SocialKey = 'instagram' | 'facebook' | 'tiktok' | 'youtube' | 'twitter';

export interface CatalogueSocialLink {
  key: SocialKey;
  label: string;
  url: string;
  handle: string;
  Icon?: ComponentType<{ style?: CSSProperties; 'aria-hidden'?: boolean }>;
}

const getSocialHandle = (url: string): string => {
  try {
    const parsed = new URL(url);
    const parts = parsed.pathname.split('/').filter(Boolean);
    const value = parts.at(-1) ?? parsed.hostname;
    return value.startsWith('@') ? value : `@${value}`;
  } catch {
    return url;
  }
};

export const buildCatalogueSocialLinks = (
  settings: SiteSettings | null | undefined,
): CatalogueSocialLink[] => {
  const candidates = [
    { key: 'instagram', label: 'Instagram', url: settings?.instagram_url, Icon: InstagramOutlined },
    { key: 'facebook', label: 'Facebook', url: settings?.facebook_url, Icon: FacebookFilled },
    { key: 'tiktok', label: 'TikTok', url: settings?.tiktok_url },
    { key: 'youtube', label: 'YouTube', url: settings?.youtube_url, Icon: YoutubeFilled },
    { key: 'twitter', label: 'X (Twitter)', url: settings?.twitter_url },
  ] satisfies Array<{
    key: SocialKey;
    label: string;
    url: string | null | undefined;
    Icon?: CatalogueSocialLink['Icon'];
  }>;

  return candidates.flatMap((entry) => {
    const url = entry.url?.trim();
    return url
      ? [{ ...entry, url, handle: getSocialHandle(url) }]
      : [];
  });
};

export const CatalogueSocialIcon = ({ link, size = 22 }: { link: CatalogueSocialLink; size?: number }) => {
  if (link.Icon) {
    return <link.Icon style={{ fontSize: size }} aria-hidden />;
  }
  return <span className="catalogue-social-icon__x" style={{ fontSize: size }} aria-hidden>{link.key === 'tiktok' ? 'T' : 'X'}</span>;
};
